import React, { useMemo, useState } from "react";
import { IShowType } from "@eagerdog/interfaces";

import Button from "../Button/Button";
import Checkbox from "../Checkbox/Checkbox";

import "./EntryFormsFilterModal.scss";

interface IProps {
  showTypes: IShowType[];
  onDownload: (showTypes?: string[]) => void;
  onCancel: () => void;
}

type FilterMode = "all" | "selected";

const formatShowType = (showType: string): string =>
  showType
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const EntryFormsFilterModal: React.FC<IProps> = ({ showTypes, onDownload, onCancel }) => {
  const [filterMode, setFilterMode] = useState<FilterMode>("all");
  const [selectedShowTypes, setSelectedShowTypes] = useState<string[]>([]);

  const availableShowTypes = useMemo(() => {
    const unique = Array.from(
      new Set((showTypes || []).map((showType) => showType.show_type).filter(Boolean))
    );
    return unique.sort((a, b) => formatShowType(a).localeCompare(formatShowType(b)));
  }, [showTypes]);

  const handleToggleShowType = (showType: string) => {
    setSelectedShowTypes((current) =>
      current.includes(showType)
        ? current.filter((value) => value !== showType)
        : [...current, showType]
    );
  };

  const handleSelectAll = () => {
    setSelectedShowTypes(availableShowTypes);
  };

  const handleSelectNone = () => {
    setSelectedShowTypes([]);
  };

  const handleModeChange = (mode: FilterMode) => {
    setFilterMode(mode);
    if (mode === "all") {
      setSelectedShowTypes([]);
    }
  };

  const handleDownload = () => {
    if (filterMode === "all") {
      onDownload(undefined);
      return;
    }

    onDownload(selectedShowTypes);
  };

  return (
    <div className="entry-forms-filter-modal">
      <h2>Download Forms</h2>
      <p>Select which show types to include in entry forms:</p>

      <div className="scrollable-content">
        <div className="filter-options">
          <label className={`radio-option ${filterMode === "all" ? "selected" : ""}`}>
            <input
              type="radio"
              name="entryFormsFilterMode"
              value="all"
              checked={filterMode === "all"}
              onChange={() => handleModeChange("all")}
            />
            <div className="option-content">
              <div className="option-title">All Show Types</div>
              <div className="option-description">
                Generate entry forms for all {availableShowTypes.length} show type
                {availableShowTypes.length !== 1 ? "s" : ""}
              </div>
            </div>
          </label>

          <label className={`radio-option ${filterMode === "selected" ? "selected" : ""}`}>
            <input
              type="radio"
              name="entryFormsFilterMode"
              value="selected"
              checked={filterMode === "selected"}
              onChange={() => handleModeChange("selected")}
            />
            <div className="option-content">
              <div className="option-title">Selected Show Types</div>
              <div className="option-description">Choose specific show types to include</div>
            </div>
          </label>
        </div>

        {filterMode === "selected" && (
          <div className="selection-section">
            <div className="section-header">
              <div className="section-title">Select Show Types</div>
              <div className="quick-actions">
                <button type="button" onClick={handleSelectAll}>
                  Select All
                </button>
                <span className="divider">|</span>
                <button type="button" onClick={handleSelectNone}>
                  Select None
                </button>
              </div>
            </div>

            <div className="show-types-list">
              {availableShowTypes.map((showType) => (
                <div key={showType} className="show-type-item">
                  <Checkbox
                    id={`entry-forms-show-type-${showType}`}
                    value={selectedShowTypes.includes(showType)}
                    onChange={() => handleToggleShowType(showType)}
                    label={formatShowType(showType)}
                  />
                </div>
              ))}
            </div>

            <div className="selection-summary">
              {selectedShowTypes.length} show type{selectedShowTypes.length !== 1 ? "s" : ""}{" "}
              selected
            </div>
          </div>
        )}
      </div>

      <div className="modal-actions">
        <Button className="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          onClick={handleDownload}
          disabled={filterMode === "selected" && selectedShowTypes.length === 0}
        >
          Download
        </Button>
      </div>
    </div>
  );
};

export default EntryFormsFilterModal;
